import { Button } from '@gerdau/hefesto/Button';
import { SearchField } from '@gerdau/hefesto/SearchField';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { Title } from '@gerdau/hefesto/Title';
import moment from 'moment';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomerSelect from '../../../../components/CustomerSelect/Facelift';
import { ApplicationState } from '../../../../store';
import {
  ELongSteelOrdersStatus,
  EOrderTab,
  ILongSteelOrderData,
  ILongSteelOrdersState,
  IOpenOrderData,
  IOrderDivision,
  IOrdersInitialInputValues
} from '../../../../store/ducks/long-steel-orders/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../store/ducks/tagging/types';
import { ECustomerTypeView } from '../../../../store/ducks/user/types';
import {
  Container,
  FilterContent,
  OrderFilterBlock,
  SearchAndActionsBlock
} from './styles';
export interface IOrdersStatus {
  id: ELongSteelOrdersStatus;
  description: string;
}

interface IOrdersFiltersProps {
  orderTab?: EOrderTab;
  orderState: ILongSteelOrdersState;
  customerViewType: ECustomerTypeView;
  onCleanFilters(): void;
  loadOrders(
    states: string[],
    emitterIds: string[],
    startDate?: string,
    endDate?: string
  ): void;
  isFiltered: boolean;
  setFilteredOrders(filteredOrders: ILongSteelOrderData[]): void;
  receiverId: string;
  setReceiverId(receiverId: string): void;
  emitterIds: string[];
  setEmitterIds(emitterIds: string[]): void;
}

const OpenOrdersFilter: React.FC<IOrdersFiltersProps> = ({
  orderState,
  receiverId,
  setReceiverId,
  isFiltered,
  setFilteredOrders,
  customerViewType,
  emitterIds,
  setEmitterIds,
  onCleanFilters,
  loadOrders,
  orderTab
}) => {
  const ordersStatusList: TSelectOptionProps[] = [
    { value: ELongSteelOrdersStatus.SHIPMENT, label: 'Em carregamento' },
    { value: ELongSteelOrdersStatus.DELIVERY, label: 'Em remessa' },
    { value: ELongSteelOrdersStatus.BILLED, label: 'Faturado' },
    { value: ELongSteelOrdersStatus.PENDING, label: 'Saldo' },
    { value: ELongSteelOrdersStatus.ALL, label: 'Todos' }
  ];

  const ordersTypeProductList: TSelectOptionProps[] =
    orderState?.divisions?.map((division: IOrderDivision) => ({
      key: division.division,
      value: division.division,
      label: division.divisionDescription
    }));
  const dispatch = useDispatch();
  const customerState = useSelector(
    (appState: ApplicationState) => appState.customers
  );
  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const userState = useSelector((state: ApplicationState) => state.user);
  const customers = useSelector((state: ApplicationState) =>
    state.customers.customers.filter(
      (customer) =>
        isUserAdmin ||
        userState.roles.ordersViewCustomers.includes(customer.customerId)
    )
  );
  const [ordersStatus, setOrdersStatus] = useState<ELongSteelOrdersStatus[]>([
    ELongSteelOrdersStatus.ALL
  ]);
  const [alreadyInitialized, setAlreadyInitialized] = useState(false);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [selectedStates, setSelectedStates] = useState<string[]>(
    orderState?.initialInputValues?.states || []
  );
  const [startSelectedDate, setStartSelectedDate] = useState<moment.Moment>(
    orderState?.initialInputValues?.startDate
      ? moment(orderState?.initialInputValues?.startDate)
      : moment()
  );
  const [endSelectedDate, setEndSelectedDate] = useState<moment.Moment>(
    orderState?.initialInputValues?.endDate
      ? moment(orderState?.initialInputValues?.endDate)
      : moment()
  );

  useEffect(() => {
    const setInitialInputValues = (
      initialInputValues: IOrdersInitialInputValues
    ) => {
      setSelectedStates(initialInputValues.states);
      setEmitterIds(initialInputValues.emitterIds);
      setReceiverId(initialInputValues.receiverId);
      setOrdersStatus([initialInputValues.orderStatus]);
      setStartSelectedDate(moment(initialInputValues.startDate));
      setEndSelectedDate(moment(initialInputValues.endDate));
      setSelectedDivisions([]);
      setSearchInput('');
      setAlreadyInitialized(true);
    };
    if (
      orderState.initialInputValues &&
      !alreadyInitialized &&
      !orderState.loading &&
      orderState.initialized &&
      customers.length > 0
    ) {
      setInitialInputValues(orderState.initialInputValues);
    } else if (orderState.loading) {
      setAlreadyInitialized(false);
    }
  }, [
    orderState.initialInputValues,
    orderState.loading,
    orderState.initialized,
    alreadyInitialized,
    orderTab,
    customers,
    customerViewType,
    setReceiverId,
    setEmitterIds
  ]);

  const prepareAndSendTagging = () => {
    const taggingParams: ITaggingParams = {
      screenName: 'LG:MeusPedidos:AcosLongos:PedidosAbertos',
      itemName: 'button:limpar_filtros',
      itemDescription: 'meus_pedidos_acosLongos_pedidosAbertos_limparFiltros',
      event: 'click'
    };
    dispatch(sendTagging(taggingParams));
  };

  const onClickClean = () => {
    setAlreadyInitialized(false);
    onCleanFilters();
    prepareAndSendTagging();
  };

  const hasChangedEmitterIds = (): boolean => {
    const initialEmitterIds = orderState?.initialInputValues?.emitterIds || [];
    const hasNewEmitter = emitterIds.some(
      (emitter) => !initialEmitterIds.includes(emitter)
    );
    return hasNewEmitter || emitterIds.length !== initialEmitterIds.length;
  };

  const hasChangedReceiverId = (): boolean => {
    const initialReceiverId = orderState?.initialInputValues?.receiverId || '';

    return receiverId !== '' && receiverId !== initialReceiverId;
  };

  const canCleanFilters = (): boolean => {
    const initialValues = orderState.initialInputValues;
    const startDate = startSelectedDate
      ? (startSelectedDate as moment.Moment).format('YYYY-MM-DD')
      : '';
    const endDate = endSelectedDate
      ? (endSelectedDate as moment.Moment).format('YYYY-MM-DD')
      : '';
    const hasChangedDates =
      (initialValues?.startDate && initialValues?.startDate !== startDate) ||
      (initialValues?.endDate && initialValues?.endDate !== endDate);

    return (
      isFiltered ||
      hasChangedDates ||
      hasChangedEmitterIds() ||
      hasChangedReceiverId() ||
      selectedDivisions.length > 0 ||
      searchInput !== '' ||
      ordersStatus.length > 0
    );
  };

  const onFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus[],
    selectedDivisions: string[],
    searchInput: string
  ) => {
    onOpenOrdersFilter(
      states,
      emitterIds,
      receiverId,
      startSelectedDate,
      endSelectedDate,
      orderStatus,
      selectedDivisions,
      searchInput
    );
  };

  const filterByReceivers = (
    orders: ILongSteelOrderData[],
    filterReceiverId: string
  ): ILongSteelOrderData[] => {
    return orders.filter((order) => order.shipTo.code === filterReceiverId);
  };

  const filterByEmitters = (
    orders: ILongSteelOrderData[]
  ): ILongSteelOrderData[] => {
    const emitterId = emitterIds[0];
    return orders.filter((order) => order.soldTo.code === emitterId);
  };

  const canLoadOrders = (): boolean => {
    const startDate = startSelectedDate.format('YYYY-MM-DD');
    const endDate = endSelectedDate.format('YYYY-MM-DD');
    return (
      orderState?.items?.length === 0 ||
      (customerViewType === ECustomerTypeView.EMITTER &&
        hasChangedEmitterIds()) ||
      ((customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
        hasChangedReceiverId()) ||
      (orderTab === EOrderTab.BILLED &&
        (startDate !== orderState.initialInputValues?.startDate ||
          endDate !== orderState.initialInputValues?.endDate))
    );
  };

  const onOpenOrdersFilter = (
    states: string[],
    emitterIds: string[],
    receiverId: string,
    startSelectedDate: moment.Moment,
    endSelectedDate: moment.Moment,
    orderStatus: ELongSteelOrdersStatus[],
    selectedDivisions: string[],
    searchInput: string
  ) => {
    if (canLoadOrders()) {
      const customerIds: string[] =
        customerViewType === ECustomerTypeView.EMITTER
          ? emitterIds
          : [receiverId];

      loadOrders(states, customerIds);
    }

    const filterByDate = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      return orders.filter((order) => {
        const orderDate = moment(order.requestedDate, 'YYYY-MM-DD');
        return (
          orderDate.isSameOrAfter(startSelectedDate) &&
          orderDate.isSameOrBefore(endSelectedDate)
        );
      });
    };

    const filterByStatus = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      let ordersFilters: IOpenOrderData[];
      ordersFilters = [];
      orderStatus?.forEach((status) => {
        const aux = orders?.filter((order) => {
          switch (status) {
            case ELongSteelOrdersStatus.PENDING: {
              const pending = order.quantities.pendingQuantity
                ? Number(order.quantities.pendingQuantity)
                : 0;
              return pending && pending > 0;
            }

            case ELongSteelOrdersStatus.DELIVERY: {
              const delivery = order.quantities.deliveryQuantity
                ? Number(order.quantities.deliveryQuantity)
                : 0;
              return delivery && delivery > 0;
            }

            case ELongSteelOrdersStatus.SHIPMENT: {
              const shipment = order.quantities.shipmentQuantity
                ? Number(order.quantities.shipmentQuantity)
                : 0;
              return shipment && shipment > 0;
            }
            case ELongSteelOrdersStatus.BILLED: {
              const billed = order.quantities.billedQuantity
                ? Number(order.quantities.billedQuantity)
                : 0;
              return billed && billed > 0;
            }
            default:
              return true;
          }
        });
        ordersFilters = [...ordersFilters, ...aux];
      });
      return ordersFilters;
    };

    const filterBySearchInput = (
      orders: IOpenOrderData[]
    ): IOpenOrderData[] => {
      return orders?.filter((order) => {
        return (
          order.sku.toString().toUpperCase().includes(searchInput) ||
          order.customerSKU.toUpperCase().includes(searchInput) ||
          order.customerOrder.toUpperCase().includes(searchInput) ||
          order.salesOrder.toString().toUpperCase().includes(searchInput) ||
          order.materialDescription.toUpperCase().includes(searchInput)
        );
      });
    };

    const filterByDivision = (orders: IOpenOrderData[]): IOpenOrderData[] => {
      return orders.filter((order) =>
        selectedDivisions.includes(order.division)
      );
    };

    let filtered: IOpenOrderData[] = orderState.items as IOpenOrderData[];

    filtered = filterByDate(filtered);

    if (customerViewType === ECustomerTypeView.EMITTER && receiverId) {
      filtered = filterByReceivers(filtered, receiverId) as IOpenOrderData[];
    }

    if (
      (customerViewType === ECustomerTypeView.RECEIVER ||
        customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) &&
      emitterIds.length > 0
    ) {
      filtered = filterByEmitters(filtered) as IOpenOrderData[];
    }

    if (searchInput !== '') {
      filtered = filterBySearchInput(filtered);
    }

    if (orderStatus?.length > 0) {
      filtered = filterByStatus(filtered);
    }

    if (selectedDivisions.length > 0) {
      filtered = filterByDivision(filtered);
    }

    setFilteredOrders(filtered);
  };

  return (
    <Container>
      <FilterContent>
        <Title level={3}>Filtros</Title>
        <SearchAndActionsBlock>
          <SearchField
            label="Procurar pedido"
            placeholder="Pesquise por Nº do pedido, Nº da OV Gerdau, Nº da nota fiscal, código ou descrição de material"
            name="search"
            inputValue={searchInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchInput(e.target.value);
              onFilter(
                selectedStates,
                emitterIds,
                receiverId,
                startSelectedDate,
                endSelectedDate,
                ordersStatus,
                selectedDivisions,
                e.target.value.toUpperCase()
              );
            }}
            disabled={orderState.loading || orderState?.items?.length === 0}
          />
        </SearchAndActionsBlock>

        <div className="inputs-container">
          <OrderFilterBlock>
            {customerViewType === ECustomerTypeView.EMITTER ? (
              <CustomerSelect
                data-testid="select-recebedor-pedido"
                title={'Recebedor do pedido'}
                placeholder="Selecionar recebedor"
                disabled={
                  customerState.loading ||
                  orderState.loading ||
                  !orderState.receivers ||
                  orderState.receivers?.length === 0
                }
                customers={orderState.receivers || []}
                onSelect={(customerId) => {
                  setReceiverId(customerId);
                  onFilter(
                    selectedStates,
                    emitterIds,
                    customerId,
                    startSelectedDate,
                    endSelectedDate,
                    ordersStatus,
                    selectedDivisions,
                    searchInput.toUpperCase()
                  );
                }}
                selectedCustomerId={receiverId}
                customStyles={{ selectTitleFontSize: '0.8rem' }}
              ></CustomerSelect>
            ) : (
              <CustomerSelect
                title={'Emissor do pedido'}
                placeholder="Selecionar emissor"
                disabled={orderState.loading || orderState?.items?.length === 0}
                customers={orderState.emitters || []}
                onSelect={(customerId) => {
                  setEmitterIds([customerId]);
                  onFilter(
                    selectedStates,
                    emitterIds,
                    customerId[0],
                    startSelectedDate,
                    endSelectedDate,
                    ordersStatus,
                    selectedDivisions,
                    searchInput.toUpperCase()
                  );
                }}
                selectedCustomerId={
                  emitterIds.length ? emitterIds[0] : undefined
                }
                customStyles={{ selectTitleFontSize: '0.8rem' }}
              ></CustomerSelect>
            )}

            <Select
              data-testid="select-status-pedido"
              isMultipleSelect
              label="Status do produto"
              placeholder={`- Selecione -`}
              name="StatusOrdersSelect"
              value={ordersStatus}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onSelect={(value: any) => {
                setOrdersStatus(value);
                onFilter(
                  selectedStates,
                  emitterIds,
                  receiverId,
                  startSelectedDate,
                  endSelectedDate,
                  value as ELongSteelOrdersStatus[],
                  selectedDivisions,
                  searchInput.toUpperCase()
                );
              }}
              options={ordersStatusList}
              disabled={orderState.loading || orderState?.items?.length === 0}
            ></Select>

            <Select
              data-testid="select-tipo-produto-aberto"
              isMultipleSelect
              label="Tipo de produto"
              placeholder={`- Selecione -`}
              name="TypeOrdersProductSelect"
              options={ordersTypeProductList}
              value={selectedDivisions}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onSelect={(value: any) => {
                setSelectedDivisions(value);
                onFilter(
                  selectedStates,
                  emitterIds,
                  receiverId,
                  startSelectedDate,
                  endSelectedDate,
                  ordersStatus,
                  value as string[],
                  searchInput.toUpperCase()
                );
              }}
              disabled={orderState.loading || orderState?.items?.length === 0}
            ></Select>
            <Button
              type="button"
              name="btnCleanFilters"
              onClick={onClickClean}
              variant="secondary"
              disabled={orderState.loading || !canCleanFilters()}
              loading={orderState.loading}
            >
              Limpar filtros
            </Button>
          </OrderFilterBlock>
        </div>
      </FilterContent>
    </Container>
  );
};

export default OpenOrdersFilter;
